import React, { useEffect, useState, useMemo } from 'react';
import { Button, Input, Select } from '..';
import {
  FiTruck,
  FiHash,
  FiCalendar,
  FiTool,
  FiBriefcase,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../Api';
import '../CompanyForm/style.css';

export default function MachineryForm({ companyId, companies = [], initialData, onSuccess, onCancel }) {
  const isEdit = !!initialData;
  const [loading, setLoading] = useState(false);
  const [serviceStations, setServiceStations] = useState([]);

  const [formData, setFormData] = useState({
    company_id: '',
    service_station_id: '',
    tag: '',
    model: '',
    brand: '',
    year: '',
    active: true,
  });

  useEffect(() => {
    if (initialData) {
      const initialCompanyId = initialData.company_id || initialData.company?.id;
      setFormData({
        company_id: initialCompanyId ? String(initialCompanyId) : '',
        service_station_id: initialData.service_station_id ? String(initialData.service_station_id) : (initialData.serviceStation?.id ? String(initialData.serviceStation.id) : ''),
        tag: initialData.tag || '',
        model: initialData.model || '',
        brand: initialData.brand || '',
        year: initialData.year || '',
        active: initialData.active ?? true,
      });
    } else if (companyId) {
        setFormData(prev => ({ ...prev, company_id: String(companyId) }));
    }
  }, [initialData, companyId]);

  useEffect(() => {
    if (formData.company_id) {
      loadServiceStations(formData.company_id);
    } else {
      setServiceStations([]);
    }
  }, [formData.company_id]);

  async function loadServiceStations(companyId) {
    try {
      const { data } = await Api.get(`/companies/${companyId}/stations`);
      setServiceStations(data || []);
    } catch (error) {
      console.error('Error loading service stations:', error);
    }
  }

  const companyOptions = useMemo(() => {
    return companies.map(c => ({
      value: String(c.id),
      label: c.corporate_name || c.trade_name || `Empresa ${c.id}`,
    }));
  }, [companies]);

  function handleChange(event) {
    const { id, value } = event.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!formData.company_id) {
        toast.error('Empresa é obrigatória.');
        return;
    }

    if (!formData.tag || !formData.model) {
      toast.error('Etiqueta e Modelo são obrigatórios.');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...formData,
        tag: formData.tag.toUpperCase(),
      };

      const targetCompanyId = formData.company_id;

      if (isEdit) {
        const originalCompanyId = initialData.company_id || initialData.company?.id || companyId;
        await Api.put(`/companies/${originalCompanyId}/machineries/${initialData.id}`, payload);
        toast.success('Maquinário atualizado com sucesso!');
      } else {
        await Api.post(`/companies/${targetCompanyId}/machineries`, payload);
        toast.success('Maquinário criado com sucesso!');
      }

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Erro ao salvar maquinário.');
    } finally {
      setLoading(false);
    }
  }

  const machineryBrands = [
    { value: 'Caterpillar', label: 'Caterpillar' },
    { value: 'Komatsu', label: 'Komatsu' },
    { value: 'John Deere', label: 'John Deere' },
    { value: 'Volvo CE', label: 'Volvo CE' },
    { value: 'Hitachi', label: 'Hitachi' },
    { value: 'Liebherr', label: 'Liebherr' },
    { value: 'Doosan', label: 'Doosan' },
    { value: 'JCB', label: 'JCB' },
    { value: 'Sany', label: 'Sany' },
    { value: 'XCMG', label: 'XCMG' },
    { value: 'Bobcat', label: 'Bobcat' },
    { value: 'Case', label: 'Case' },
    { value: 'New Holland', label: 'New Holland' },
    { value: 'Hyundai Construction', label: 'Hyundai Construction' },
    { value: 'Outros', label: 'Outros' },
  ];

  return (
    <form className="company-form-component" onSubmit={handleSubmit}>
      <div className="form-body">
        <h3 className="section-title" style={{ marginTop: '0px' }}>
          <FiTool style={{ marginRight: '8px' }} />
          Dados do Maquinário
        </h3>

        <div className="form-grid">
          <Select
            id="company_id"
            label="Empresa *"
            placeholder="SELECIONE UMA EMPRESA..."
            value={formData.company_id}
            onChange={handleChange}
            icon={FiBriefcase}
            options={companyOptions}
            searchable
          />

          <Select
            id="service_station_id"
            label="Posto de Serviço"
            placeholder="Selecione o Posto"
            value={formData.service_station_id}
            onChange={handleChange}
            icon={FiTruck}
            options={serviceStations.map(s => ({
              value: String(s.id),
              label: s.name || s.trade_name || `Posto ${s.id}`
            }))}
            searchable
            disabled={!formData.company_id}
          />

          <Input
            id="tag"
            label="Etiqueta *"
            placeholder="TAG-1234"
            value={formData.tag}
            onChange={handleChange}
            icon={FiHash}
          />

          <Input
            id="model"
            label="Modelo *"
            placeholder="Ex: Escavadeira XYZ"
            value={formData.model}
            onChange={handleChange}
            icon={FiTool}
          />

          <Select
            id="brand"
            label="Marca"
            placeholder="Selecione a Marca"
            value={formData.brand}
            onChange={handleChange}
            icon={FiTool}
            options={machineryBrands}
            searchable
          />

          <Input
            id="year"
            label="Ano"
            placeholder="Ex: 2023"
            value={formData.year}
            onChange={handleChange}
            icon={FiCalendar}
            type="number"
          />

          <Select
            id="active"
            label="Status"
            placeholder="Selecione"
            value={formData.active ? 'true' : 'false'}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, active: e.target.value === 'true' }))
            }
            options={[
              { value: 'true', label: 'Ativo' },
              { value: 'false', label: 'Inativo' },
            ]}
          />
        </div>
      </div>

      <div className="modal-actions">
        <Button 
            type="button" 
            className="cancel-button" 
            onClick={onCancel}
            disabled={loading}
        >
          Cancelar
        </Button>
        <Button 
            type="submit" 
            disabled={loading}
        >
          {loading ? 'Salvando...' : (isEdit ? 'Salvar Alterações' : 'Criar Maquinário')}
        </Button>
      </div>
    </form>
  );
}
